import React, { useState, useEffect, useCallback } from "react";
import cn from "classnames";
import styles from "./AbsenceAlerts.module.sass";
import Card from "../../../components/Card";
import Icon from "../../../components/Icon";
import { getCompanyForCurrentUser } from "../../../services/company.service";
import { getFoldersByCompanyId } from "../../../services/folder.service";
import { pb } from "../../../services/pocketbase";

const AbsenceAlerts = ({ className }) => {
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAbsences = useCallback(async () => {
    try {
      // Récupérer l'entreprise connectée
      const company = await getCompanyForCurrentUser();
      
      if (!company?.id) {
        setAbsences([]);
        return;
      }

      // Récupérer les dossiers de l'entreprise pour avoir les étudiants liés
      const folders = await getFoldersByCompanyId(company.id);
      
      if (!folders || folders.length === 0) {
        setAbsences([]);
        return;
      }

      // Extraire les IDs des étudiants liés par contrat
      const studentIds = folders
        .map(f => f.student_id)
        .filter(id => id);

      if (studentIds.length === 0) {
        setAbsences([]);
        return;
      }

      // Récupérer les absences de ces étudiants (derniers 30 jours)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const studentFilter = studentIds.map(id => `participant_id="${id}"`).join(' || ');
      
      const attendances = await pb.collection('attendances').getList(1, 10, {
        filter: `(${studentFilter}) && status="Absent" && created>="${thirtyDaysAgo.toISOString()}"`,
        sort: '-created',
        expand: 'participant_id,session_id'
      });

      // Formater les absences pour l'affichage
      const formattedAbsences = attendances.items.map(att => {
        const student = att.expand?.participant_id;
        const session = att.expand?.session_id;
        
        return {
          id: att.id,
          studentName: student ? `${student.first_name || ''} ${student.last_name || ''}`.trim() : 'Étudiant inconnu',
          sessionTitle: session?.title || 'Session',
          date: att.created ? new Date(att.created).toLocaleDateString('fr-FR') : '',
          isJustified: att.status_justification === 'Absence justifiée'
        };
      });

      setAbsences(formattedAbsences);
    } catch (error) {
      console.error("Erreur lors de la récupération des absences:", error);
      setAbsences([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAbsences();

    // Listener temps réel
    pb.collection('attendances').subscribe('*', () => fetchAbsences());

    return () => {
      pb.collection('attendances').unsubscribe();
    };
  }, [fetchAbsences]);

  return (
    <Card
      className={cn(styles.card, className)}
      title="Alertes d'absences"
      classTitle="title-red"
    >
      <div className={styles.alerts}>
        {loading ? (
          <div className={styles.loading}>Chargement...</div>
        ) : absences.length === 0 ? (
          <div className={styles.empty}>
            <Icon name="check" size="24" />
            <p>Aucune absence récente pour vos apprentis</p>
          </div>
        ) : (
          <div className={styles.list}>
            {absences.map((absence) => (
              <div 
                key={absence.id} 
                className={styles.item}
              >
                <div className={styles.iconWrapper}>
                  <Icon name="profile-circle" size="20" />
                </div>
                <div className={styles.content}>
                  <div className={styles.studentName}>{absence.studentName}</div>
                  <div className={styles.sessionInfo}>
                    {absence.sessionTitle} - {absence.date}
                  </div>
                </div>
                <div className={styles.badge}>
                  {absence.isJustified ? (
                    <span className={styles.justified}>Justifiée</span>
                  ) : (
                    <span className={styles.notJustified}>Non justifiée</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

export default AbsenceAlerts;
